import { useEffect, useMemo, useState } from 'react'
import {
  BarChartCard,
  ColumnChartCard,
  ControlledDropDown,
  LineChartCard,
  SvgReload,
  SvgVerticalSplitter,
} from '@ui-kit'
import { AppComponentProps } from '../../utils/types'
import Highcharts, { SeriesColumnOptions, SeriesLineOptions } from 'highcharts'

import {
  REPORT_COLORS,
  REPORT_DAYS,
  REPORTS,
  SLA_COLORS,
} from '../../utils/constants'
import {
  AgentProductivityApiResponse,
  BarChartApiResponse,
  DropdownType,
  fetchTypeForCase,
  FilterDropdownProps,
  getProgramById,
  getReportsData,
  hideLoader,
  IntakeClosureApiResponse,
  IntakeVsClosureReportRequestType,
  ReportFormPayload,
  setProgramDropdown,
  showLoader,
} from '@app-store'
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from '../../utils/agent.store'
import { useForm } from 'react-hook-form'
import {
  formatLocalDate,
  getWeekBoundaries,
  subtractDaysFromDate,
} from '../../utils/date.utils'
import { isEmpty } from 'lodash'
import { useLocation } from 'react-router-dom'

const formatDay = (date: string) => formatLocalDate(date, 'ddd')

const generateLineChartData = (data: IntakeClosureApiResponse | null) => {
  const intakeMap: Record<string, number> = {}
  const closureMap: Record<string, number> = {}

  data?.intake?.forEach((item) => {
    const day = formatDay(item?.intakeDate)
    intakeMap[day] = (intakeMap?.[day] || 0) + item?.intakeCount
  })

  data?.closure?.forEach((item) => {
    const day = formatDay(item.case_closure_date)
    closureMap[day] = (closureMap?.[day] || 0) + item.case_count
  })

  const categories: string[] = Object.values(REPORT_DAYS)

  const intakeData = categories?.map((day) => intakeMap?.[day] || 0)
  const closureData = categories?.map((day) => closureMap?.[day] || 0)

  return {
    categories,
    series: [
      {
        name: REPORTS?.INTAKE,
        type: 'line',
        data: intakeData,
        marker: { enabled: true, symbol: 'circle' },
      },
      {
        name: REPORTS?.CLOSURE,
        type: 'line',
        data: closureData,
        marker: { enabled: true, symbol: 'circle' },
        color: REPORT_COLORS.GREEN,
      },
    ],
  }
}

const parseDurationToMinutes = (durationStr: string): number => {
  const extract = (regex: RegExp): number => {
    const match = regex.exec(durationStr)
    return parseInt(match?.[1] ?? '0', 10)
  }

  const years = extract(/(\d+)\s+years/)
  const months = extract(/(\d+)\s+mons/)
  const days = extract(/(\d+)\s+days/)
  const hours = extract(/(\d+)\s+hours/)
  const minutes = extract(/(\d+)\s+mins/)

  return years * 525600 + months * 43800 + days * 1440 + hours * 60 + minutes
}

const generateBarChartData = (data: BarChartApiResponse | null) => {
  const categories = data?.services?.map(
    (item) =>
      item?.service_name?.charAt(0)?.toUpperCase() +
      item?.service_name?.slice(1)
  )

  const tatData = data?.services?.map((item) => ({
    customKey: `Total Count: ${item?.case_count}`,
    y: parseDurationToMinutes(item?.avg_adjusted_duration),
  }))
  return {
    categories,
    series: [
      {
        name: REPORTS?.TAT_KEY,
        type: 'column',
        data: tatData,
        colorByPoint: true,
      },
    ],
  }
}

const generateHorizontalBarData = (
  apiData: Record<
    string,
    { total: number; at_risk: number; overdue: number; on_track: number }
  > | null
) => {
  const defaultSeries = [
    { key: REPORTS?.ON_TRACK_KEY, name: REPORTS?.ON_TRACK },
    { key: REPORTS?.AT_RISK_KEY, name: REPORTS?.AT_RISK },
    { key: REPORTS?.OVERDUE_KEY, name: REPORTS?.OVERDUE },
  ]

  if (!apiData) {
    return {
      categories: [],
      series: defaultSeries.map(({ key, name }) => ({
        name,
        type: 'bar',
        data: [],
        color: SLA_COLORS[key as keyof typeof SLA_COLORS],
      })),
    }
  }

  const categories = Object.keys(apiData)

  const series = defaultSeries.map(({ key, name }) => ({
    name,
    type: 'bar' as const,
    data: categories.map((key) => {
      const item = apiData[key]
      switch (name) {
        case REPORTS?.ON_TRACK:
          return item?.on_track ?? 0
        case REPORTS?.AT_RISK:
          return item?.at_risk ?? 0
        case REPORTS?.OVERDUE:
          return item?.overdue ?? 0
        default:
          return 0
      }
    }),
    color: SLA_COLORS[key as keyof typeof SLA_COLORS],
  }))

  return { categories, series }
}

const defaultColors = Highcharts.getOptions().colors || []

const generateAgentProductivityData = (
  data: AgentProductivityApiResponse | null
) => {
  const categories = data?.agentProductivity?.map((agent) => agent?.user_name)
  const totalCounts = data?.agentProductivity?.map(
    (agent) => agent?.total_count
  )

  return {
    categories,
    series: [
      {
        name: REPORTS?.VOLUME,
        type: 'column',
        data: totalCounts,
        colorByPoint: true,
        colors: [...defaultColors].reverse(),
      },
    ],
  }
}

const ReportsPage: React.FC<AppComponentProps> = () => {
  const dispatch = useAppDispatch()
  const ReportFormData: ReportFormPayload = {
    duration_intake_closure: '',
    duration_tat: '',
    duration_agent_productivity: '',
    duration_sla_comp: '',
    intake_type: '',
    case_type_intake_closure: '',
    product_type_sla_comp: '',
  }
  const {
    formState: { errors },
    control,
    setValue,
    reset,
    watch,
  } = useForm({ defaultValues: ReportFormData })
  const { start, end } = getWeekBoundaries()
  const location = useLocation()
  const lastWeekStart = subtractDaysFromDate(start, 7, 'YYYY-MM-DD HH:mm:ss')
  const lastWeekEnd = subtractDaysFromDate(end, 7, 'YYYY-MM-DD HH:mm:ss')

  const currentWeekPayload = {
    filter: {
      fields: null,
      search_criteria: [],
    },
    date_range: {
      start_date: start,
      end_date: end,
    },
  }

  const lastWeekPayload = {
    filter: {
      fields: null,
      search_criteria: [],
    },
    date_range: {
      start_date: lastWeekStart,
      end_date: lastWeekEnd,
    },
  }

  const selectedClientProgram = useAppSelector(
    (state: RootState) => state?.clientPrograms?.selectedClientPrograms
  )
  const [commonParamsForIntakeClosure, setCommonParamsForIntakeClosure] =
    useState<IntakeVsClosureReportRequestType>({
      ...currentWeekPayload.date_range,
      program_id: selectedClientProgram?.program_id ?? -1,
      intake_types: REPORTS?.INTAKE_TYPES_ARRAY,
    })
  const [commonParamsForSlaComp, setCommonParamsForSlaComp] =
    useState<IntakeVsClosureReportRequestType>({
      ...currentWeekPayload.date_range,
      program_id: selectedClientProgram?.program_id ?? -1,
    })
  const initialState = {
    ...currentWeekPayload.date_range,
    program_id: selectedClientProgram?.program_id ?? -1,
  }

  const [apiIntakeClosureData, setApiIntakeClosureData] =
    useState<IntakeClosureApiResponse | null>(null)
  const [apiTurnAroundData, setApiTurnAroundData] =
    useState<BarChartApiResponse | null>(null)
  const [apiSlaCompData, setApiSlaCompData] = useState<Record<
    string,
    {
      total: number
      at_risk: number
      overdue: number
      on_track: number
    }
  > | null>(null)
  const [apiAgentProdData, setApiAgentProdData] =
    useState<AgentProductivityApiResponse | null>(null)
  const intakeClosure = useMemo(
    () => generateLineChartData(apiIntakeClosureData),
    [apiIntakeClosureData]
  )
  const turnaround = useMemo(
    () => generateBarChartData(apiTurnAroundData),
    [apiTurnAroundData]
  )
  const slaComp = useMemo(
    () => generateHorizontalBarData(apiSlaCompData),
    [apiSlaCompData]
  )
  const agentProd = useMemo(
    () => generateAgentProductivityData(apiAgentProdData),
    [apiAgentProdData]
  )
  const [productOptions, setProductOptions] = useState<DropdownType<string>[]>(
    []
  )
  const [caseTypeOptions, setCaseTypeOptions] = useState<
    DropdownType<string>[]
  >([])
  const [refetchKey, setRefetchKey] = useState<number>(0)

  const getProducts = async (id: number) => {
    const response = await getProgramById({ id })
    const productOptns = (
      response?.data?.product_services?.map(
        (product: { product_id: number; trademarked_name?: string }) => ({
          label: product?.trademarked_name ?? '',
          value: Number(product?.product_id),
        })
      ) ?? []
    )?.sort(
      (
        a: {
          label: string
          value: number
        },
        b: {
          label: string
          value: number
        }
      ) => {
        if (!a.label) return -1
        if (!b.label) return 1
        return a.label.toLowerCase().localeCompare(b.label.toLowerCase())
      }
    )

    setProductOptions(productOptns)
  }
  const getCaseTypes = async (e: string) => {
    if (e === '-1') return
    const type = await fetchTypeForCase(e)
    const modifiedType = type?.data
      ?.map((item: { service_name: string; id: number }) => ({
        label: item.service_name,
        value: item.id,
        id: item.id,
      }))
      ?.sort(
        (
          a: {
            label: string
            value: number
            id: number
          },
          b: {
            label: string
            value: number
            id: number
          }
        ) => {
          if (!a.label) return -1
          if (!b.label) return 1
          return a.label.toLowerCase().localeCompare(b.label.toLowerCase())
        }
      )
    setCaseTypeOptions(modifiedType)
  }

  const fetchIntakeClosure = async (
    payload: IntakeVsClosureReportRequestType
  ) => {
    try {
      dispatch(showLoader())
      const result = await getReportsData({
        ...payload,
        program_id: selectedClientProgram?.program_id ?? -1,
        report_type: REPORTS?.INTAKE_VS_CLOSURE_KEY,
      })

      setApiIntakeClosureData(result.data)
    } catch (err) {
      console.error(REPORTS?.INTAKE_VS_CLOSURE_API_FAIL, err)
    } finally {
      dispatch(hideLoader())
    }
  }
  const fetchProgramBasedReports = async (
    payload: IntakeVsClosureReportRequestType,
    key?: string
  ) => {
    try {
      dispatch(showLoader())

      const program_id = selectedClientProgram?.program_id ?? -1
      const basePayload = { ...payload, program_id }

      const getReport = (report_type: string) =>
        getReportsData({ ...basePayload, report_type })

      if (!key || key === REPORTS.TAT_KEY) {
        const tatResponse = await getReport(REPORTS.TAT_KEY)
        setApiTurnAroundData(tatResponse.data)
      }

      if (!key || key === REPORTS.AGENT_PRODUCTIVITY_KEY) {
        const agentProdResponse = await getReport(
          REPORTS.AGENT_PRODUCTIVITY_KEY
        )
        setApiAgentProdData(agentProdResponse.data)
      }
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const fetchSLAComp = async (payload: IntakeVsClosureReportRequestType) => {
    try {
      dispatch(showLoader())
      const result = await getReportsData({
        ...payload,
        program_id: selectedClientProgram?.program_id ?? -1,
        report_type: REPORTS?.SLA_KEY,
      })

      setApiSlaCompData(result.data)
    } catch (err) {
      console.error(REPORTS?.ERROR_FETCHING_SLA_DATA, err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const handleFilterChartData = async (
    key: string,
    value: string | string[],
    type: string,
    duration?: string
  ) => {
    const isLastWeek = duration === REPORTS.LAST_WEEK
    const range = isLastWeek
      ? lastWeekPayload.date_range
      : currentWeekPayload.date_range

    const buildPayload = (
      base: IntakeVsClosureReportRequestType,
      overrides = {}
    ) => ({
      ...base,
      ...range,
      ...overrides,
    })

    switch (type) {
      case REPORTS.INTAKE_VS_CLOSURE_KEY:
        if (duration) {
          const payload = buildPayload(commonParamsForIntakeClosure)
          setCommonParamsForIntakeClosure(payload)
          fetchIntakeClosure(payload)
        } else {
          const payload = {
            ...commonParamsForIntakeClosure,
            [key]: value,
          }
          setCommonParamsForIntakeClosure(payload)
          fetchIntakeClosure(payload)
        }
        break

      case REPORTS.SLA_KEY:
        if (duration) {
          const payload = buildPayload(commonParamsForSlaComp)
          fetchSLAComp(payload)
          setCommonParamsForSlaComp(payload)
        } else {
          const payload = {
            ...commonParamsForSlaComp,
            [key]: value,
          }
          fetchSLAComp(payload)
          setCommonParamsForSlaComp(payload)
        }
        break

      case REPORTS.RESET_FILTERS_INTAKE_CLOSURE:
        {
          const intakeResetPayload = {
            ...initialState,
            intake_types: REPORTS?.INTAKE_TYPES_ARRAY,
          }
          setCommonParamsForIntakeClosure(intakeResetPayload)
          fetchIntakeClosure(intakeResetPayload)
          setValue('intake_type', '')
          setValue('case_type_intake_closure', '')
          setValue('duration_intake_closure', REPORTS.THIS_WEEK)
        }
        break

      case REPORTS.RESET_FILTERS_SLA_COMP:
        fetchSLAComp(initialState)
        setValue('product_type_sla_comp', '')
        setValue('duration_sla_comp', REPORTS.THIS_WEEK)
        break
      case REPORTS.RESET_FILTERS_TAT:
        {
          const payload = { ...initialState, report_type: REPORTS.TAT_KEY }
          fetchProgramBasedReports(payload, REPORTS.TAT_KEY)
          setValue('duration_tat', REPORTS.THIS_WEEK)
        }
        break
      case REPORTS.RESET_FILTERS_AGENT_PRODCUTIVITY:
        {
          const payload = {
            ...initialState,
            report_type: REPORTS.AGENT_PRODUCTIVITY_KEY,
          }
          fetchProgramBasedReports(payload, REPORTS.AGENT_PRODUCTIVITY_KEY)
          setValue('duration_agent_productivity', REPORTS.THIS_WEEK)
        }
        break

      default:
        if (duration) {
          const payload = buildPayload(initialState, { report_type: type })
          fetchProgramBasedReports(payload, type)
        }
        break
    }
  }

  const handleReloadButtonClick = () => {
    setRefetchKey((prev) => prev + 1)
  }

  useEffect(() => {
    selectedClientProgram?.program_id &&
      getProducts(selectedClientProgram?.program_id ?? -1)
    fetchProgramBasedReports(initialState)
    fetchIntakeClosure({
      ...currentWeekPayload.date_range,
      program_id: selectedClientProgram?.program_id ?? -1,
      intake_types: REPORTS?.INTAKE_TYPES_ARRAY,
    })
    fetchSLAComp(initialState)
    getCaseTypes(selectedClientProgram?.program_id?.toString() ?? '-1')
    reset()
  }, [
    selectedClientProgram?.program_id,
    selectedClientProgram?.tenant_id,
    refetchKey,
  ])

  useEffect(() => {
    dispatch(setProgramDropdown({ isVisible: true }))
  }, [location])

  return (
    <div>
      <div className="flex h-10 mb-4 items-center justify-between">
        <span className="flex text-sm leading-6 font-semibold">
          {REPORTS?.REPORTS}
        </span>
        <div className="flex items-center justify-center">
          <button
            className="cursor-pointer flex"
            onClick={handleReloadButtonClick}
          >
            <SvgReload />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-6 p-4">
        {/* Intake vs Closure */}

        <div
          className={`border rounded-xl p-4 bg-white shadow flex flex-col gap-4`}
        >
          <h2 className="text-sm font-bold mb-2 leading-6">
            {REPORTS?.INTAKE_VS_CLOSURE_LABEL}
          </h2>
          <div className="flex flex-col gap-4">
            <div className="w-full flex justify-between items-center flex-wrap gap-4">
              <div className="flex gap-4 items-center flex-wrap">
                <span className="flex items-center">{REPORTS?.FILTERS}</span>
                <WeekFilterDropdown
                  onChange={(value) =>
                    handleFilterChartData(
                      '',
                      [],
                      REPORTS?.INTAKE_VS_CLOSURE_KEY,
                      value
                    )
                  }
                  control={control}
                  errors={errors}
                  name="duration_intake_closure"
                  watch={watch}
                />
                <SvgVerticalSplitter />
                <IntakeTypeFilterDropdown
                  onChange={(value) =>
                    handleFilterChartData(
                      'intake_types',
                      !value?.length ? REPORTS.INTAKE_TYPES_ARRAY : [value],
                      REPORTS?.INTAKE_VS_CLOSURE_KEY
                    )
                  }
                  options={[
                    { label: REPORTS?.FAX, value: REPORTS?.FAX_KEY },
                    { label: REPORTS?.WEB, value: REPORTS?.WEB_KEY },
                  ]}
                  control={control}
                  errors={errors}
                  watch={watch}
                />
                <SvgVerticalSplitter />
                <CaseTypeFilterDropdown
                  onChange={(value) => {
                    handleFilterChartData(
                      'service_ids',
                      !isEmpty(value?.toString()) ? [value] : [],
                      REPORTS?.INTAKE_VS_CLOSURE_KEY
                    )
                  }}
                  options={caseTypeOptions}
                  control={control}
                  errors={errors}
                  watch={watch}
                />
              </div>
              <button
                className="flex text-red-500 cursor-pointer"
                onClick={() =>
                  handleFilterChartData(
                    '',
                    [],
                    REPORTS.RESET_FILTERS_INTAKE_CLOSURE
                  )
                }
              >
                {REPORTS?.RESET_FILTERS}
              </button>
            </div>
            <div>
              <LineChartCard
                title={REPORTS?.INTAKE_VS_CLOSURE_LABEL}
                categories={intakeClosure.categories}
                series={intakeClosure.series as SeriesLineOptions[]}
                xAxisTitle={REPORTS?.WEEKLY}
                yAxisTitle={REPORTS?.VOLUME}
              />
            </div>
          </div>
        </div>

        {/* Turn Around Time */}
        <div
          className={`border rounded-xl p-4 bg-white shadow flex flex-col gap-4`}
        >
          <h2 className="text-sm font-bold mb-2 leading-6">
            {REPORTS?.TURN_AROUND_TIME_LABEL}
          </h2>
          <div className="flex flex-col gap-10">
            <div className="h-7 w-full flex justify-between items-center flex-wrap">
              <div className="flex gap-6 flex-wrap">
                <span className="flex items-center">{REPORTS?.FILTERS}</span>
                <WeekFilterDropdown
                  onChange={(value) =>
                    handleFilterChartData('', [], REPORTS?.TAT_KEY, value)
                  }
                  control={control}
                  errors={errors}
                  name={'duration_tat'}
                  watch={watch}
                />
              </div>
              <button
                className="flex text-red-500 cursor-pointer"
                onClick={() =>
                  handleFilterChartData(
                    '',
                    [],
                    REPORTS.RESET_FILTERS_TAT,
                    REPORTS.THIS_WEEK
                  )
                }
              >
                {REPORTS?.RESET_FILTERS}
              </button>
            </div>
            <ColumnChartCard
              title={REPORTS?.TURN_AROUND_TIME_LABEL}
              categories={turnaround?.categories ?? []}
              series={turnaround?.series as SeriesColumnOptions[]}
              yAxisTitle={REPORTS?.TAT_IN_MINS}
              xAxisTitle={REPORTS?.SERVICES}
            />
          </div>
        </div>

        {/* SLA Comp */}

        <div
          className={`border rounded-xl p-4 bg-white shadow flex flex-col gap-4`}
        >
          <h2 className="text-sm font-bold mb-2 leading-6">
            {REPORTS?.SLA_COMPLETION}
          </h2>
          <div className="h-7 w-full flex justify-between items-center flex-wrap">
            <div className="flex gap-6 items-center flex-wrap">
              <span className="flex items-center">{REPORTS?.FILTERS}</span>
              <WeekFilterDropdown
                onChange={(value) =>
                  handleFilterChartData('', [], REPORTS.SLA_KEY, value)
                }
                control={control}
                errors={errors}
                name={'duration_sla_comp'}
                watch={watch}
              />
              <SvgVerticalSplitter />
              <CaseTypeFilterDropdown
                onChange={(value) =>
                  handleFilterChartData('product_ids', value, REPORTS.SLA_KEY)
                }
                control={control}
                errors={errors}
                options={productOptions}
                placeholder={'Product'}
                name={'product_type_sla_comp'}
                watch={watch}
              />
            </div>
            <button
              className="flex text-red-500 cursor-pointer"
              onClick={() =>
                handleFilterChartData('', [], REPORTS.RESET_FILTERS_SLA_COMP)
              }
            >
              {REPORTS?.RESET_FILTERS}
            </button>
          </div>
          <BarChartCard
            title={REPORTS?.SLA_COMPLETION}
            categories={slaComp?.categories ?? []}
            series={slaComp?.series as Highcharts.SeriesBarOptions[]}
            yAxisTitle={REPORTS?.VOLUME}
            xAxisTitle={REPORTS?.SERVICES}
          />
        </div>

        {/* Agent Productivity */}

        <div
          className={`border rounded-xl p-4 bg-white shadow flex flex-col gap-4`}
        >
          <h2 className="text-sm font-bold mb-2 leading-6">
            {REPORTS?.AGENT_PRODUCTIVITY_LABEL}
          </h2>
          <div className="h-7 w-full flex justify-between items-center flex-wrap">
            <div className="flex gap-6 flex-wrap">
              <span className="flex items-center">{REPORTS?.FILTERS}</span>
              <WeekFilterDropdown
                onChange={(value) =>
                  handleFilterChartData(
                    '',
                    [],
                    REPORTS?.AGENT_PRODUCTIVITY_KEY,
                    value
                  )
                }
                control={control}
                errors={errors}
                name={'duration_agent_productivity'}
                watch={watch}
              />
            </div>
            <button
              className="flex text-red-500 cursor-pointer"
              onClick={() =>
                handleFilterChartData(
                  '',
                  [],
                  REPORTS.RESET_FILTERS_AGENT_PRODCUTIVITY,
                  REPORTS.THIS_WEEK
                )
              }
            >
              {REPORTS?.RESET_FILTERS}
            </button>
          </div>
          <ColumnChartCard
            title={REPORTS?.AGENT_PRODUCTIVITY_LABEL}
            categories={agentProd?.categories ?? []}
            series={agentProd?.series as SeriesColumnOptions[]}
            yAxisTitle={REPORTS?.VOLUME}
            xAxisTitle={REPORTS?.AGENTS}
          />
        </div>
      </div>
    </div>
  )
}

const WeekFilterDropdown: React.FC<FilterDropdownProps> = ({
  onChange,
  control,
  errors,
  name = 'duration_tat',
  watch,
}) => {
  const handleChange = (name: string, label: string) => {
    onChange && onChange(name, label)
  }
  return (
    <div className="flex h-7 min-w-[8.5rem] reports-filter-dropdown">
      <ControlledDropDown
        control={control}
        label={''}
        options={[
          { label: REPORTS?.THIS_WEEK, value: REPORTS?.THIS_WEEK },
          { label: REPORTS?.LAST_WEEK, value: REPORTS?.LAST_WEEK },
        ]}
        name={name}
        onChange={(e) => handleChange(e?.value, e?.label)}
        errors={errors}
        className="bg-primary-500 rounded h-8 text-accent-0 flex"
        isBordered={false}
        defaultValue={watch(name) === '' ? REPORTS?.THIS_WEEK : watch(name)}
      />
    </div>
  )
}

const IntakeTypeFilterDropdown: React.FC<FilterDropdownProps> = ({
  onChange,
  options,
  control,
  errors,
}) => {
  const handleChange = (value: string, label: string) => {
    onChange && onChange(value, label)
  }
  return (
    <div className="flex h-7 min-w-[8.5rem] reports-filter-dropdown">
      <ControlledDropDown
        control={control}
        label={''}
        options={options ?? []}
        name={'intake_type'}
        onChange={(e) => handleChange(e?.value, e?.label)}
        errors={errors}
        className="flex h-8 rounded"
        placeholder={REPORTS?.INTAKE_TYPE}
        isBordered={false}
        borderClassName="border rounded border-primary-500"
      />
    </div>
  )
}

const CaseTypeFilterDropdown: React.FC<FilterDropdownProps> = ({
  options,
  onChange,
  control,
  errors,
  placeholder,
  name,
}) => {
  const handleChange = (name: string, label: string) => {
    onChange && onChange(name, label)
  }

  return (
    <div className="flex h-7 min-w-[8.5rem] reports-filter-dropdown">
      <ControlledDropDown
        control={control}
        label={''}
        options={options ?? []}
        name={name ?? 'case_type_intake_closure'}
        onChange={(e) => handleChange(e?.value, e?.label)}
        errors={errors}
        className="flex h-8 rounded"
        placeholder={placeholder ?? REPORTS?.CASE_TYPE}
        isBordered={false}
        isSearchable
        borderClassName="border rounded border-primary-500"
      />
    </div>
  )
}

export default ReportsPage
